import { useLayoutEffect, useRef } from 'react';

/**
 * Misst die tatsächliche Höhe eines fixierten/sticky Headers (z.B. `#app-header`,
 * eine seiteneigene sticky Titelzeile) oder eines Fixed-Bottom-Banners per
 * ResizeObserver und veröffentlicht sie als CSS-Custom-Property auf <html>,
 * damit abhängige `top`/`padding-bottom`/`scroll-padding-*`-Regeln (siehe u.a.
 * App.css `--app-navbar-height`/`--page-sticky-header-height`,
 * CookieConsentBanner.css/InstallAppBanner.css) den tatsächlich reservierten
 * Platz exakt an den echten Inhalt anpassen.
 *
 * MAJOR-5-Fix: feste Pixelwerte (z.B. 160px) reichen bei umbrechendem Text
 * und/oder gestapelten Buttons auf schmalen Viewports unter Umständen bei
 * Weitem nicht aus — der Banner würde dann Fußzeileninhalte verdecken und
 * `scroll-padding-bottom` ein fokussiertes Element am Seitenende nicht mehr
 * zuverlässig freihalten. Der feste Wert bleibt als CSS-Fallback (`var(--x,
 * 160px)`) erhalten, solange kein ResizeObserver verfügbar ist (ältere
 * Browser/Testumgebungen) oder bevor der erste Messwert vorliegt.
 *
 * Misst per `useLayoutEffect` (statt `useEffect`): die Variable treibt bei
 * einigen Verwendern (z.B. `--app-navbar-height`, `--page-sticky-header-height`)
 * das `top` von above-the-fold sichtbaren Sticky-Elementen — mit `useEffect`
 * wäre beim ersten Paint kurzzeitig der CSS-Fallback wirksam, was zu einem
 * sichtbaren Layout-Sprung führen kann.
 *
 * @param {boolean} visible    - ob der Banner/Header aktuell sichtbar ist/gerendert wird
 * @param {string}  cssVarName - Name der CSS-Custom-Property, z.B. '--cookie-banner-height'
 * @returns {React.RefObject<HTMLElement>} an das Wurzelelement des Banners/Headers zu binden
 */
export function useBannerHeightCssVar(visible, cssVarName) {
  const bannerRef = useRef(null);

  useLayoutEffect(() => {
    if (!visible || !bannerRef.current) {
      return undefined;
    }
    const element = bannerRef.current;

    const updateHeight = () => {
      document.documentElement.style.setProperty(cssVarName, `${element.getBoundingClientRect().height}px`);
    };
    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      // Kein ResizeObserver verfügbar: der CSS-Fallback-Wert (siehe JSDoc
      // oben) greift weiterhin, nur ohne dynamische Nachmessung.
      return undefined;
    }
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(cssVarName);
    };
  }, [visible, cssVarName]);

  return bannerRef;
}
